/* eslint-disable require-yield */
import axios from 'axios';
import Cookies from 'js-cookie';
import getIn from 'lodash/get';
import noop from 'lodash/noop';
import { push } from 'react-router-redux';
import { addMiddleware } from 'redux-dynamic-middlewares';
import { call, delay, put, select, take } from 'redux-saga/effects';

import * as D from '../../deps';
import * as ApplicationUtil from '../../utils/Application';

import offlineHandler from './OfflineHandler';

let pollingInterval = D.AppConstants.NETWORK_POLL_INTERVAL_DEFAULT_MS;

const LOOP = true;

const log = D.Logger.child({ component: 'application/sagas' });

export function* openBrowserAutoplayBlockModal() {
  const isBrowserAutoplayBlockDetected = yield select(ApplicationUtil.isBrowserAutoPlayDetected);
  let autoPlayBlockModalShownCookiesCount =
    Cookies.get(D.AppConstants.AUTOPLAY_BROWSER_BLOCK_MODAL_COUNT_COOKIE) || 0;
  const isBrowserAutoPlayBlockOpportunityHappened = yield select(
    ApplicationUtil.isBrowserAutoPlayBlockOpportunityHappened,
  );
  if (isBrowserAutoplayBlockDetected && !isBrowserAutoPlayBlockOpportunityHappened) {
    yield put(D.AppActions.setBrowserAutoplayBlockOpportunityHappened());
    // We are capturing initial_playback_clicked separately to capture how many opportunities that we are missing to show modal due to cookie setting.
    yield put(
      D.getInDomain('analytics.actions.logBrowserAutomaticPlayback')(
        D.AppConstants.AUTOPLAY_BROWSER_BLOCK_ANALYTICS_ACTION.INITIAL_PLAYBACK_CLICKED,
      ),
    );
    if (
      autoPlayBlockModalShownCookiesCount < D.AppConstants.AUTOPLAY_BROWSER_BLOCK_MODAL_MAX_COUNT
    ) {
      const expireTimestamp = new Date(new Date().setMonth(new Date().getMonth() + 6));
      Cookies.set(
        D.AppConstants.AUTOPLAY_BROWSER_BLOCK_MODAL_COUNT_COOKIE,
        ++autoPlayBlockModalShownCookiesCount,
        { expires: expireTimestamp },
      );
      const { dispatch } = D.getStore();
      const modalConfig = {
        showMeHow: () => {
          dispatch(
            D.getInDomain('analytics.actions.logBrowserAutomaticPlayback')(
              D.AppConstants.AUTOPLAY_BROWSER_BLOCK_ANALYTICS_ACTION.SHOW_ME_HOW,
            ),
          );
          dispatch(D.ModalActions.closeModal());
          // User got instruction for how to unblock browser so set modal count to max so we don't show it in future.
          Cookies.set(
            D.AppConstants.AUTOPLAY_BROWSER_BLOCK_MODAL_COUNT_COOKIE,
            D.AppConstants.AUTOPLAY_BROWSER_BLOCK_MODAL_MAX_COUNT,
            { expires: expireTimestamp },
          );
        },
        notNow: () => {
          dispatch(
            D.getInDomain('analytics.actions.logBrowserAutomaticPlayback')(
              D.AppConstants.AUTOPLAY_BROWSER_BLOCK_ANALYTICS_ACTION.NOT_NOW,
            ),
          );
          dispatch(D.ModalActions.closeModal());
        },
      };
      const clientInfo = yield select(D.ClientUtils.getClientInfo);
      const modifiedState = {
        clientInfo,
      };
      yield put(D.ModalActions.openBrowserAutoplayBlockModal(modalConfig, modifiedState));
      yield put(
        D.getInDomain('analytics.actions.logBrowserAutomaticPlayback')(
          D.AppConstants.AUTOPLAY_BROWSER_BLOCK_ANALYTICS_ACTION.MODAL_DISPLAY,
        ),
      );
    }
  }
}

export function* openLinkedPlaylistUnlockedModal({
  payload: {
    source: { playlist },
  },
}) {
  const { dispatch } = D.getStore();
  const playlistUrl = playlist.get('shareableUrlPath');
  yield put(
    D.getInDomain('analytics.actions.logPremiumPlaylistCoachmark')(
      D.AppConstants.PREMIUM_PLAYLIST_ANALYTICS_ACTION.MODAL_DISPLAY,
    ),
  );
  const closeModal = () => {
    dispatch(
      D.getInDomain('analytics.actions.logPremiumPlaylistCoachmark')(
        D.AppConstants.PREMIUM_PLAYLIST_ANALYTICS_ACTION.CLOSE,
      ),
    );
    dispatch(D.ModalActions.closeModal());
  };
  const modalConfig = {
    name: playlist.get('name'),
    closeModal,
    viewPlaylist: () => {
      dispatch(
        D.getInDomain('analytics.actions.logPremiumPlaylistCoachmark')(
          D.AppConstants.PREMIUM_PLAYLIST_ANALYTICS_ACTION.SHOW_PLAYLIST,
        ),
      );
      dispatch(push(playlistUrl));
      closeModal();
    },
  };
  yield put(D.ModalActions.openLinkedPlaylistUnlockedModal(modalConfig));
}

export function* detectBrowserAutoplayBlocked(action) {
  const errorType = getIn(action, 'data.ERROR_OBJ.commonType');
  const isBrowserAutoPlayBlockOpportunityHappened = yield select(
    ApplicationUtil.isBrowserAutoPlayBlockOpportunityHappened,
  );
  if (
    errorType === D.AudioPlayerConstants.CommonAudioErrorCodes.BROWSER_AUTOPLAY_BLOCKED &&
    !isBrowserAutoPlayBlockOpportunityHappened
  ) {
    yield call(D.putAndWait, D.AppActions.setIsBrowserAutoplayBlockDetected(true));
    yield put(
      D.getInDomain('analytics.actions.logBrowserAutomaticPlayback')(
        D.AppConstants.AUTOPLAY_BROWSER_BLOCK_ANALYTICS_ACTION.PLAYBACK_BLOCKED,
      ),
    );
  }

  if (errorType === D.AudioPlayerConstants.CommonAudioErrorCodes.BROWSER_AUTOPLAY_BLOCKED) {
    const previousPlayedHarness = yield select(
      D.getInDomain('analytics.selectors.getPreviousPlayedHarness', noop),
    );
    const trackData = yield select(D.getInDomain('playback.utils.getTrackDataForLogging'));
    const logData = {
      previousPlayedHarness,
      ...trackData,
    };
    log.warn('Autoplay is blocked', logData);
  }
}

export function* handleStartupQueryParameters() {
  const params = yield select(D.UrlUtils.getQueryParameters);
  yield put(D.AppActions.setStartupQueryParameters(params));
}

export function* setReferrerCookie() {
  Cookies.set('http_referrer', document.referrer);
}

export function* watchActions() {
  const updater = D.RecentActionsSnapshot.actionsSnapshotUpdater();
  while (LOOP) {
    const action = yield take();
    updater(action);
  }
}

export function* addPageVisibilityHandler() {
  D.PageVisibilityUtils.addVisibilityHandler(() => {
    D.getStore().dispatch(D.AppActions.pageVisibilityChanged());
  });
}

export function* goOnline() {
  pollingInterval = D.AppConstants.NETWORK_POLL_INTERVAL_DEFAULT_MS;
  yield put(D.OverlayActions.closeOfflineNotification());
  const lastOfflineAction = yield select(D.AppUtils.getLastOfflineAction);
  if (lastOfflineAction) {
    // Make sure to allow last action to be dispatched first via putAndWait to avoid race condition.
    yield call(D.putAndWait, lastOfflineAction);
    yield put(D.AppActions.setLastOfflineAction(null));
  }
}

export function* goOffline() {
  pollingInterval = D.AppConstants.NETWORK_POLL_INTERVAL_OFFLINE_MS;
}

export function* pokeNetworkWithEndpoint(endpoint) {
  try {
    yield call(axios.get, endpoint, {
      timeout: D.AppConstants.NETWORK_PULSE_TIMEOUT_MS,
    });
    yield put(D.AppActions.switchAppOnline());
  } catch (error) {
    const hasNetworkError = error.message === D.ServiceErrorConstants.NETWORK_ERROR;
    const hasTimedOut = error.code === D.ServiceErrorConstants.CONNECTION_ABORTED;
    if (hasNetworkError || hasTimedOut) {
      yield put(D.AppActions.switchAppOffline());
    }
  }
}

// Infinite loop to check status of network
export function* pollNetwork() {
  const isWebPingRadioHealthEnabled = yield select(D.ListenerUtils.isWebPingRadioHealthEnabled);
  const isAuthenticatedListener = yield select(D.ListenerUtils.isAuthenticatedListener);
  let endpoint = `${
    D.AppConstants.NETWORK_PULSE_ENDPOINT
  }?f=${D.AppUtils.generateCachebustNumber()}`;

  // pollNetwork also runs for anon listeners so we need to check if the listener is authenticated before
  // logging exposure. Otherwise we would end up sending the anon listener id to the ab framework.
  if (isAuthenticatedListener) {
    if (isWebPingRadioHealthEnabled) {
      endpoint = D.AppConstants.NETWORK_RADIO_HEALTH_ENDPOINT;
    }

    // Logging diversion point for isWebPingRadioHealthEnabled experiment
    yield put(D.AppActions.tryPokingNetwork());
  }

  yield call(pokeNetworkWithEndpoint, endpoint);
  yield delay(pollingInterval);
  yield call(pollNetwork);
}

export function* onExternalRedirect({ payload: { url, openWindow } }) {
  yield call(D.RouteUtils.navigate, url, openWindow);
}

export function* onFirstAudioPlay() {
  yield call(addMiddleware, offlineHandler);
}
